<script lang="ts" setup>
import { useDebounce } from '~/composables/useDebounce'

interface IProps {
  modelValue?: string
  readonly?: boolean
  type: string
}

withDefaults(defineProps<IProps>(), {
  modelValue: '',
  readonly: false,
})

const emits = defineEmits([ 'update:modelValue', 'searchRedirect' ])

const searchInput = ref<HTMLInputElement>()
const searchDebounceTime = 500

const inputValue = ref('')

const clearInput = () => {
  inputValue.value = ''
  emits('update:modelValue', '')
}

const { debounce } = useDebounce()

const debounceInput = debounce(() => {
  emits('update:modelValue', inputValue.value)
}, searchDebounceTime)

const handleInput = (event: InputEvent) => {
  const eventTarget = event.target as HTMLInputElement
  inputValue.value = eventTarget.value

  debounceInput()
}

onMounted(() => {
  searchInput.value?.focus()
})
</script>

<template>
  <div
    class="input-container"
    :class="type"
  >
    <div class="search-icon">
      <FontAwesomeIcon
        icon="search"
        size="xs"
        class="text-primary"
      />
    </div>
    <slot name="prefix" />
    <input
      class="input"
      type="search"
      placeholder="Search by Txn Hash, Block, Address, Token, NFT"
      :readonly="readonly"
      ref="searchInput"
      :value="inputValue"
      @input="handleInput"
      @keydown.enter="$emit('searchRedirect')"
      @keydown.esc="clearInput"
    >
    <button
      v-if="inputValue.length > 0"
      class="size-6 aspect-square rounded-lg text-primary/40 hover:bg-primary/5 transition-opacity duration-300 ease-[ease]"
    >
      <FontAwesomeIcon
        icon="times"
        size="xs"
        @click="clearInput"
      />
    </button>
    <div
      v-if="$slots.suffix"
      class="shrink-0"
    >
      <slot name="suffix" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.input-container {
  @apply flex items-center rounded-lg;

  .search-icon {
    @apply pointer-events-none mr-2 flex shrink-0 items-center overflow-hidden;
  }

  .input {
    @apply block w-full border-0 bg-transparent outline-none hover:cursor-text focus:ring-0 text-lg
    placeholder:text-primary/40 text-primary;

    &::-webkit-search-cancel-button {
      -webkit-appearance: none;
    }
  }
}

.input-container.home {
  @apply border border-white px-6 py-4 text-primary bg-white/5;

  .input {
    @apply text-xl;
  }
}

.input-container.general {
  @apply w-full border-0 bg-primary/20 px-4 py-2 text-primary placeholder:text-primary/40;

  .search-icon {
    @apply text-primary/40;
  }
}

.input.search {
  @apply bg-transparent w-full;
}

</style>